<template>
    <div class="filters-list / flex">
        <template v-if="isProjectListDesktopScreen">
            <div class="filters-list_desktop / flex" :class="{isAtEnd, isAtStart}">
                <div ref="filterContainer" class="filters-list_desktop_wrapper / flex" @wheel="handleFiltersScroll">
                    <FiltersGroup />
                </div>
                <Transition name="fade">
                    <div v-if="!isAtEnd" class="filters-list_desktop_overflow end / flex">
                        <VJoyIcon name="chevron-right" color="neutral" @click.capture="scrollFiltersToEnd" />
                    </div>
                </Transition>
                <Transition name="fade">
                    <div v-if="!isAtStart" class="filters-list_desktop_overflow start / flex">
                        <VJoyIcon name="chevron-left" color="neutral" @click.capture="scrollFiltersToStart" />
                    </div>
                </Transition>
            </div>
            <GhostButton class="reset-button" size="m" :disabled="!isAnyFilterSelected" @click="projectListFilters.handleResetAllCategories">
                {{ t('projectclient.projectlist.topbar.button.reset') }}
            </GhostButton>
        </template>

        <div v-else class="filters-list_small-screen / flex">
            <SmallScreenFilterPlaceholder @click="handleOpenSmallScreenModal" />
        </div>
    </div>
    <SmallScreenFilterModal :open="showSmallScreenModal" @close="handleCloseSmallScreenModal" />
</template>

<script setup lang="ts">
    import {ref, useTemplateRef, watch} from 'vue';
    import {useScreenSizeStore} from '@common/stores';
    import {GhostButton} from '@common/ui';
    import {useProjectsListFiltersStore} from '@features/projects-list';
    import {storeToRefs} from 'pinia';
    import {nextTick, useTranslation} from '#imports';
    import {useScrollContainer} from '@malt/utils';
    import {VJoyIcon} from '@maltjoy/core-vue';
    import FiltersGroup from './FiltersGroup.vue';
    import SmallScreenFilterModal from '../small-screen/SmallScreenFilterModal.vue';
    import SmallScreenFilterPlaceholder from '../small-screen/SmallScreenFilterPlaceholder.vue';

    const {t} = useTranslation();

    const screenSizeStore = useScreenSizeStore();
    const {isProjectListDesktopScreen} = storeToRefs(screenSizeStore);

    const projectListFilters = useProjectsListFiltersStore();
    const {isAnyFilterSelected} = storeToRefs(projectListFilters);

    const initialSmallScreenModalRender = ref(false);
    const showSmallScreenModal = ref(false);

    const filterContainer = useTemplateRef('filterContainer');

    const {isAtEnd, isAtStart, isScrollingX, scrollLeft, scrollWidth, startListening, stopListening} = useScrollContainer({target: filterContainer});

    watch([isProjectListDesktopScreen, scrollWidth], async (value) => {
        await nextTick();
        if (isProjectListDesktopScreen.value) {
            startListening();
        } else {
            stopListening();
        }
    });

    function handleOpenSmallScreenModal() {
        if (!initialSmallScreenModalRender.value) {
            initialSmallScreenModalRender.value = true;
        }
        showSmallScreenModal.value = true;
    }

    function handleCloseSmallScreenModal() {
        showSmallScreenModal.value = false;
    }

    function scrollFiltersToEnd() {
        filterContainer.value?.scrollTo({left: scrollLeft.value + 200, behavior: 'smooth'});
    }

    function scrollFiltersToStart() {
        filterContainer.value?.scrollTo({left: scrollLeft.value - 200, behavior: 'smooth'});
    }

    function handleFiltersScroll(event: WheelEvent) {
        if (isScrollingX.value && !event.deltaY) {
            event.stopImmediatePropagation();
        }
        if (event.deltaY) {
            event.preventDefault();
            filterContainer.value?.scrollTo({left: scrollLeft.value - event.deltaY});
        }
    }

    watch(isProjectListDesktopScreen, (value) => {
        if (value) {
            showSmallScreenModal.value = false;
        }
    });
</script>

<style lang="scss" scoped>
    .filters-list {
        flex-flow: row nowrap;
        align-items: center;
        column-gap: var(--joy-core-spacing-2);

        &_desktop {
            &_overflow {
                content: '';
                position: absolute;
                height: 100%;
                width: 100px;
                pointer-events: none;
                z-index: 10;
                align-items: center;
                padding: 0 var(--joy-core-spacing-1);

                .joy-icon {
                    cursor: pointer;
                    pointer-events: visiblepainted;

                    &:hover {
                        color: var(--joy-color-secondary-50);
                    }
                }

                &.end {
                    right: 0;
                    background: linear-gradient(to right, rgb(255 255 255 / 0%), var(--joy-color-neutral-0));
                    justify-content: flex-end;
                }

                &.start {
                    left: 0;
                    background: linear-gradient(to left, rgb(255 255 255 / 0%), var(--joy-color-neutral-0));
                    justify-content: flex-start;
                }
            }

            &_wrapper {
                flex-flow: row nowrap;
                align-items: center;
                flex: 0 1 auto;
                overflow-x: auto;
                border-radius: var(--joy-core-radius-3);
                scrollbar-width: none;

                .reset-button {
                    margin-left: var(--joy-core-spacing-2);
                }
            }
        }

        > .joy-button {
            flex: 0 0 auto;
            text-wrap: nowrap;
        }
    }
</style>
